import { useState } from 'react';
import { ChartTooltip, useChartHover } from './ChartTooltip.jsx';
import { SERIES, TRACK, LABEL, LABEL_STRONG, FONT } from './chartColors.js';

// A donut for parts of ONE whole — outcomes, dismissal reasons, work
// arrangement. Deliberately NOT offered for anything else.
//
// A pie is a poor comparison tool: people read angles badly, and two pies side
// by side are worse than two bars. It has one genuine strength, which is showing
// how a single total divides, and that is the only job it is given here. The raw
// count and the percentage are printed next to every slice, so nothing depends
// on judging an angle.
//
// Donut rather than pie because the hole carries the total, which is the number
// the operator actually wants first.
//
// Interaction: hover AND keyboard focus highlight a slice and open the shared
// ChartTooltip. Each slice is a real tab stop with its own accessible name, so
// the chart is operable without a mouse — the legend is not a substitute.

const TAU = Math.PI * 2;

function arcPath(cx, cy, rOuter, rInner, from, to) {
  // A full circle cannot be drawn as a single arc — start and end coincide and
  // the browser renders nothing. Two half arcs, or the whole chart disappears
  // exactly when one category accounts for everything.
  if (to - from >= TAU - 1e-6) {
    const mid = from + Math.PI;
    return [arcPath(cx, cy, rOuter, rInner, from, mid), arcPath(cx, cy, rOuter, rInner, mid, from + TAU)].join(' ');
  }
  const p = (r, a) => [cx + r * Math.cos(a), cy + r * Math.sin(a)];
  const [x0, y0] = p(rOuter, from);
  const [x1, y1] = p(rOuter, to);
  const [x2, y2] = p(rInner, to);
  const [x3, y3] = p(rInner, from);
  const large = to - from > Math.PI ? 1 : 0;
  return [
    `M ${x0} ${y0}`,
    `A ${rOuter} ${rOuter} 0 ${large} 1 ${x1} ${y1}`,
    `L ${x2} ${y2}`,
    `A ${rInner} ${rInner} 0 ${large} 0 ${x3} ${y3}`,
    'Z',
  ].join(' ');
}

export default function DonutChart({
  data = [],
  label = (d) => d.label,
  value = (d) => d.value,
  colorOf,
  size = 168,
  thickness = 26,
  centerLabel = 'total',
  ariaLabel,
}) {
  const { hover, show, hide } = useChartHover();
  const [activeIdx, setActiveIdx] = useState(null);

  const rows = data.map((d, i) => ({ d, i, v: Math.max(0, value(d, i) || 0) })).filter((r) => r.v > 0);
  const total = rows.reduce((n, r) => n + r.v, 0);
  if (!total) return null;

  const cx = size / 2;
  const cy = size / 2;
  const rOuter = size / 2 - 2;
  const rInner = rOuter - thickness;

  let cursor = -Math.PI / 2; // start at twelve o'clock, where a reader starts
  const slices = rows.map((r) => {
    const angle = (r.v / total) * TAU;
    const seg = { ...r, from: cursor, to: cursor + angle, pct: (r.v / total) * 100 };
    cursor += angle;
    return seg;
  });

  return (
    <div className="relative flex flex-wrap items-center gap-5">
      <svg
        width={size}
        height={size}
        role="img"
        aria-label={ariaLabel}
        style={{ display: 'block', fontFamily: FONT, flexShrink: 0 }}
      >
        <circle cx={cx} cy={cy} r={(rOuter + rInner) / 2} fill="none" stroke={TRACK} strokeWidth={thickness} />
        {slices.map((s) => {
          const fill = colorOf ? colorOf(s.d, s.i) : SERIES.accent;
          const on = activeIdx === s.i;
          const name = `${label(s.d, s.i)}: ${s.v} of ${total} (${s.pct.toFixed(0)}%)`;
          const enter = () => {
            setActiveIdx(s.i);
            const mid = (s.from + s.to) / 2;
            show(cx + Math.cos(mid) * rOuter, cy + Math.sin(mid) * rOuter, name);
          };
          const leave = () => {
            setActiveIdx(null);
            hide();
          };
          return (
            <path
              key={s.i}
              d={arcPath(cx, cy, on ? rOuter : rOuter - 2, rInner, s.from, s.to)}
              fill={fill}
              tabIndex={0}
              role="img"
              aria-label={name}
              style={{ cursor: 'default', transition: 'd 120ms' }}
              onMouseEnter={enter}
              onMouseLeave={leave}
              onFocus={enter}
              onBlur={leave}
            />
          );
        })}
        <text x={cx} y={cy - 2} textAnchor="middle" fontSize="22" fontWeight="600" fill={LABEL_STRONG}>
          {total}
        </text>
        <text x={cx} y={cy + 14} textAnchor="middle" fontSize="9" fill={LABEL}>
          {centerLabel}
        </text>
      </svg>

      {/* The legend carries the numbers, so no reading depends on an angle. */}
      <ul className="min-w-0 flex-1 space-y-1 text-xs">
        {slices.map((s) => (
          <li
            key={s.i}
            className={'flex items-baseline gap-2 rounded px-1 py-0.5 ' + (activeIdx === s.i ? 'bg-slate-800/60' : '')}
            onMouseEnter={() => setActiveIdx(s.i)}
            onMouseLeave={() => setActiveIdx(null)}
          >
            <span
              aria-hidden="true"
              className="mt-1 h-2 w-2 shrink-0 rounded-sm"
              style={{ background: colorOf ? colorOf(s.d, s.i) : SERIES.accent }}
            />
            <span className="min-w-0 flex-1 truncate text-slate-300">{label(s.d, s.i)}</span>
            <span className="shrink-0 tabular-nums text-slate-400">{s.v}</span>
            <span className="w-9 shrink-0 text-right tabular-nums text-slate-500">{s.pct.toFixed(0)}%</span>
          </li>
        ))}
      </ul>
      <ChartTooltip hover={hover} width={size} />
    </div>
  );
}
